/**
 * Font families, per platform.
 *
 * The web and native ends of this disagree in a way that is easy to get wrong once and then never
 * notice. On the web, `font-family: Figtree` plus `font-weight: 600` picks the right `@font-face`
 * out of global.css and the browser does the matching. Android does not do that matching: it
 * looks up a family by EXACT NAME, so a 600-weight Figtree has to be registered and asked for as
 * its own family called `Figtree-600`, or the text renders in the 400 and the weight is silently
 * lost. iOS is more forgiving but behaves the same way with `expo-font`.
 *
 * So every weight is registered under its own name in App.tsx, and `familyFor` is the single
 * place that turns "display face, semibold" into the string that names it.
 */
import { Platform } from 'react-native';

export type Face = 'display' | 'sans';
export type Weight = 400 | 500 | 600 | 700;

/**
 * The two families, matching `tailwind.config.js`'s `font-display` / `font-sans`.
 *
 * Fraunces is the restyle's display face, from both concept sheets' first graft — "these fonts are
 * the most readable and should be used". Figtree is unchanged and carries all the body copy.
 */
export const FAMILY: Record<Face, string> = { display: 'Fraunces', sans: 'Figtree' };

/**
 * The italic display face, registered under its own name.
 *
 * Only the display face has one, and only native needs it named: on the web the `italic` class's
 * `font-style: italic` picks the italic `@font-face` out of `global.css` on its own, and Fraunces
 * ships its italic as a separate variable file. Android matches a loaded font by EXACT FAMILY NAME
 * and will happily synthesise a slant instead, which on a serif at 42px reads as a rendering
 * fault — so `familyFor` asks for it by name and `ui/text.tsx` is what reads the class.
 */
export const ITALIC_FAMILY = `${FAMILY.display}-Italic`;

/**
 * What to put in `fontFamily` for this face at this weight and style.
 *
 * Null on the web: there is nothing to do there. The CSS variable already names the family and
 * the browser picks the weight and the style, and forcing `Figtree-600` would name a family no
 * `@font-face` rule declares — which falls all the way back to the system font.
 *
 * Italic only reaches the display face, because only the display face has one. Asking for an
 * italic body face returns the upright Figtree rather than a family name nothing registered:
 * NativeWind's own `fontStyle: 'italic'` then has the last word there, which is a synthesised
 * slant on a sans and reads fine.
 */
export function familyFor(face: Face, weight: Weight, italic = false): string | null {
  if (Platform.OS === 'web') return null;
  const family = italic && face === 'display' ? ITALIC_FAMILY : FAMILY[face];
  return weight === 400 ? family : `${family}-${weight}`;
}

/**
 * The weight a NativeWind class string is asking for.
 *
 * The type scale sets weights through `font-medium` / `font-semibold` / `font-bold` at the call
 * site, which on native ends up as a `fontWeight` the loaded font cannot honour. Reading it back
 * out of the class string is how the right family gets chosen instead.
 */
/**
 * Whether a NativeWind class string is asking for italic.
 *
 * The same problem as the weight, one step along: `italic` is a class at the call site and on
 * native it becomes a `fontStyle` the loaded font cannot honour, so it has to be read back and
 * turned into a family name. Last one wins, the way the cascade would — `italic not-italic` is
 * upright — and a variant prefix (`web:italic`) is stripped so the base name is what is compared.
 */
export function italicFromClassName(className?: string): boolean {
  if (!className) return false;
  let italic = false;
  for (const token of className.split(/\s+/)) {
    const name = token.slice(token.lastIndexOf(':') + 1);
    if (name === 'italic') italic = true;
    else if (name === 'not-italic') italic = false;
  }
  return italic;
}

/**
 * Whether a NativeWind class string is asking for tabular figures.
 *
 * `tabular-nums` is the one utility in this file's business that NativeWind does NOT carry across:
 * it parses `font-family`, `font-weight` and `font-style` but not `font-variant-numeric`, and
 * Tailwind emits that property as a var-composed shorthand which it would not understand anyway.
 * So the web takes the class and native takes a `fontVariant` from this — see `ui/text.tsx`.
 *
 * It matters where it is used: every one of them is a NUMERAL that changes value in place — a
 * score, a countdown, a peel counter — and proportional figures make those jitter on every poll.
 */
export function tabularFromClassName(className?: string): boolean {
  if (!className) return false;
  return className.split(/\s+/).some((token) => token.slice(token.lastIndexOf(':') + 1) === 'tabular-nums');
}

export function weightFromClassName(className?: string): Weight {
  if (!className) return 400;
  if (/\bfont-bold\b/.test(className)) return 700;
  if (/\bfont-semibold\b/.test(className)) return 600;
  if (/\bfont-medium\b/.test(className)) return 500;
  return 400;
}

/**
 * Every family/weight pair `expo-font` has to be told about, mapped to its asset.
 *
 * Built here rather than inline in App.tsx so this file stays the one place that knows the naming
 * convention — a registration and a lookup that disagree is a font that loads and is never used.
 */
export function fontMap(assets: {
  display: Record<Weight, number>;
  sans: Record<Weight, number>;
  /** The display face's italics. Optional, because only one of the two faces has any. */
  displayItalic?: Record<Weight, number>;
}): Record<string, number> {
  const map: Record<string, number> = {};
  for (const face of ['display', 'sans'] as const) {
    for (const weight of [400, 500, 600, 700] as const) {
      map[weight === 400 ? FAMILY[face] : `${FAMILY[face]}-${weight}`] = assets[face][weight];
    }
  }
  if (assets.displayItalic) {
    for (const weight of [400, 500, 600, 700] as const) {
      map[weight === 400 ? ITALIC_FAMILY : `${ITALIC_FAMILY}-${weight}`] = assets.displayItalic[weight];
    }
  }
  return map;
}
